import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useWebSocket from 'react-use-websocket';

import PageWrapper from '@components/PageWrapper';
import LoadingProgress from '@components/LoadingProgress';
import BottomButtonsWrapper from '@components/BottomButtonsWrapper';
import { useNotification } from '@components/Notification';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import {
  IEventChangedPayload,
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';
import { IEvent } from '@interfaces/event';
import { AnalyticsType } from '@interfaces/analytics';
import { getExternalUrlHash, compareTokenHash } from '@helpers/externalUrlHash';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import SelectEventPageView from './SelectEventPageView';
import {
  useClientSpecificCarouselEventsTransform,
  useClientSpecificNoEventsNotification,
  useClientSpecificSelectEvent,
} from '@hooks/clientSpecificUtils';
import { groupEvents } from '@helpers/events';
import {
  applyEventChanged,
  filterVisibleEvents,
  isVisibleInCarousel,
  overlayAppliedStatuses,
  pruneAppliedChanges,
  IAppliedEventChange,
} from './helper';
import { removeLocalStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';

const RELOAD_DEBOUNCE_MS = 400;

// A burst of broadcasts must not keep pushing the refetch out forever: past this
// point the pending timer is left to fire.
const RELOAD_MAX_WAIT_MS = 2_000;

const SelectEventPage = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IEvent>();
  const hasLoadedRef = useRef(false);
  const appliedChangesRef = useRef<Record<string, IAppliedEventChange>>({});
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refetchPendingSinceRef = useRef(0);

  const {
    user,
    token,
    trackAnalytics,
    setCurrentScreen,
    getWebsocketUrl,
    getWebSocketOptions,
  } = useMytaverse();

  const { lastJsonMessage } = useWebSocket(
    getWebsocketUrl,
    getWebSocketOptions({ withReconnection: false }),
  );
  const { showNotification, getBadRequestNotification } = useNotification();
  const { pathname } = useLocation();
  const { t: translate } = useTranslation();
  const showNoEventsNotification = useClientSpecificNoEventsNotification();

  const carouselEventsTransform = useClientSpecificCarouselEventsTransform();
  const getClientSpecificSelectEvent = useClientSpecificSelectEvent();

  useEffect(() => {
    setCurrentScreen(CurrentScreenEnum.DEFAULT);

    removeLocalStorageValue(
      StorageValues.TransitionLoadingScreen,
      StorageTypes.Session,
    );
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);

    const participantEvents =
      await ParticipantsService.getCurrentParticipantEvents();

    if (
      participantEvents.events.length === 0 &&
      user &&
      showNoEventsNotification
    ) {
      const message = `The email address ${
        user.email || ''
      } is not invited to any worlds. Are you sure this is the correct email address?`;
      showNotification(getBadRequestNotification({ message }));

      console.error(message);
    }

    const fetchedEvents = participantEvents.events.map((participantEvent) => ({
      ...(participantEvent.event as IEvent),
      isDisabled: participantEvent.isDisabled,
      state: participantEvent.state,
      inviteStartTime:
        participantEvent.startDate || participantEvent.event?.startTime || 0,
    }));

    const events = overlayAppliedStatuses(
      fetchedEvents,
      appliedChangesRef.current,
    ).filter(isVisibleInCarousel);

    groupEvents(events);

    // Assigned unconditionally: skipping the empty case left the last deleted
    // experience on screen until a reload.
    setEvents(filterVisibleEvents(events));

    setLoading(false);
  }, [setLoading, setEvents, pathname, user]);

  const goToNextScreen = useCallback(async () => {
    if (!selectedEvent) {
      return;
    }

    sendCustomSentryBreadcrumb({
      message: `select event "${selectedEvent.name}"`,
    });

    trackAnalytics(AnalyticsType.EventSelect, {
      eventId: selectedEvent.eventId,
    });

    getClientSpecificSelectEvent(selectedEvent);
  }, [trackAnalytics, selectedEvent, getClientSpecificSelectEvent]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadEvents();
    }

    const externalEventTokenHash = getExternalUrlHash();

    if (externalEventTokenHash) {
      compareTokenHash(token, externalEventTokenHash).then(
        (isTokenHashValid) => {
          if (!isTokenHashValid) {
            const message = 'External event auth token hash is invalid!';
            showNotification(getBadRequestNotification({ message }));
          }
        },
      );
    }
  }, [loadEvents]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadEvents();
    }, 15_000);

    return () => {
      clearInterval(interval);
    };
  }, [loadEvents]);

  // Debounced: publishing an experience writes its status several times in a
  // row, and every write is broadcast.
  const scheduleReload = useCallback(() => {
    if (refetchTimerRef.current) {
      if (Date.now() - refetchPendingSinceRef.current >= RELOAD_MAX_WAIT_MS) {
        return;
      }

      clearTimeout(refetchTimerRef.current);
    } else {
      refetchPendingSinceRef.current = Date.now();
    }

    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      loadEvents();
    }, RELOAD_DEBOUNCE_MS);
  }, [loadEvents]);

  useEffect(
    () => () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const msg = lastJsonMessage as LastWebsocketJsonMessageType | null;

    if (msg?.action === WebsocketAction.EventReleaseStatusChanged) {
      loadEvents();
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    if (
      (lastJsonMessage as LastWebsocketJsonMessageType | null)?.action !==
      WebsocketAction.EventChanged
    ) {
      return;
    }

    const payload = lastJsonMessage as unknown as IEventChangedPayload;
    const alreadyApplied = appliedChangesRef.current[payload.eventId];

    if (alreadyApplied && payload.timestamp <= alreadyApplied.timestamp) {
      return;
    }

    appliedChangesRef.current = {
      ...pruneAppliedChanges(appliedChangesRef.current),
      [payload.eventId]: {
        status: payload.event.status,
        timestamp: payload.timestamp,
        // Local clock: the payload timestamp is the server's and cannot be
        // compared with the overlay window measured here.
        appliedAt: Date.now(),
      },
    };

    const applied = applyEventChanged(events, payload);

    setEvents(applied.events);

    if (applied.needsRefetch) {
      scheduleReload();
    }
  }, [lastJsonMessage, events, scheduleReload]);

  useEffect(() => {
    if (
      selectedEvent &&
      !events.find((e) => e.eventId === selectedEvent.eventId)
    ) {
      setSelectedEvent(undefined);
    }
  }, [events]);

  const carouselEvents = useMemo(
    () => carouselEventsTransform(events),
    [events],
  );

  if (!carouselEvents.length && loading) {
    return <LoadingProgress height="30vh" />;
  }

  const BottomElement = (
    <BottomButtonsWrapper
      hideLeftButton
      rightButtonDisabled={!selectedEvent}
      rightButtonText={translate('next')}
      rightButtonClick={goToNextScreen}
    />
  );

  return (
    <PageWrapper
      bottomElement={BottomElement}
      title={
        !events.length
          ? translate('eventPage.titleNoEvents')
          : translate('eventPage.title')
      }
    >
      {/*<Assistant isSelectEventPage={true} />*/}
      <SelectEventPageView
        events={carouselEvents}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
      />
    </PageWrapper>
  );
};

export default SelectEventPage;
